import moment from "moment";
import {
  EngineMode,
  MenuSelection,
  SelectedOffer,
  SelectionSteps,
} from "../types";
import { Subject } from "rxjs";
import selectionGuestsService from "../services/selection-guests-service";
import selectionCalendarDateService from "../services/selection-calendar-date.service";
import { requestParamsImpl } from "../services/request-params.service";
import { moduleSettings } from "./module-settings.store";

require("moment/min/locales.min");

class SelectionStore {
  private _date: moment.Moment | undefined;
  private _calendarDate: moment.Moment = moment().startOf("month");
  private _time: string = "";
  private _guests: number = 0;
  private _children: number = 0;
  private _period: string | null = null;
  private _area: string | null = null;
  private _selectedMenus: Array<MenuSelection> = [];
  private _menusCount: number = 0;
  private _selectedOffer: SelectedOffer | null = null;
  private _backButtonClicked: boolean = false;
  public selectedOfferChanged: Subject<SelectedOffer | null> = new Subject();
  public isAreaSelectionDisabled: boolean = false;

  public resetStore() {
    selectionGuestsService.setDefaultSelectedGuest();

    //!\ reset -this.date, this.calendar
    selectionCalendarDateService.setDefaultDate();
    selectionCalendarDateService.setDefaultTime();

    this.setDefaultAreaId();

    this.time = "";
    this.period = null;
    this.selectedOffer = null;
    this.selectedMenus = [];
    this.menusCount = 0;
  }

  public computeDefaultAreaId(): string | null {
    const areaId = moduleSettings.areaId;
    const overwrittenArea = requestParamsImpl.overwrittenArea();
    return areaId ? areaId : overwrittenArea ? overwrittenArea : null;
  }

  public setDefaultAreaId() {
    this.area = this.computeDefaultAreaId();
  }

  public get guests(): number {
    return this._guests;
  }

  public set guests(guestNumber: number) {
    this._guests = guestNumber;
  }

  public get children(): number {
    return this._children;
  }

  public set children(childrenNumber: number) {
    this._children = childrenNumber;
  }

  public get date(): moment.Moment | undefined {
    return this._date;
  }

  public set date(newDate: moment.Moment | undefined) {
    this._date = newDate;
  }

  public get time() {
    return this._time;
  }

  public set time(newTime: string) {
    this._time = newTime;
  }

  public set selectedOffer(offer: SelectedOffer | null) {
    this._selectedOffer = offer;
    this.selectedOfferChanged.next(offer);
  }

  public get selectedOffer(): SelectedOffer | null {
    return this._selectedOffer;
  }

  public addOffer(offer: SelectedOffer) {
    if (this.selectedOffer) {
      this.selectedOffer.isSelected = false;
    }
    offer.isSelected = true;
    this.selectedOffer = offer;
  }

  public removeOffer() {
    if (this.selectedOffer) {
      this.selectedOffer.isSelected = false;
    }
    this.selectedOffer = null;
  }

  public set selectedMenus(menus: Array<MenuSelection>) {
    this._selectedMenus = menus;
  }

  public get selectedMenus(): Array<MenuSelection> {
    return this._selectedMenus;
  }

  public updateSelectedMenus(changedMenuId: number, newQuantity: number) {
    const index = this._selectedMenus.findIndex(
      (menu: MenuSelection) => menu.menuId === changedMenuId
    );
    if (index !== -1) {
      this._selectedMenus[index].count = newQuantity;
      this.menusCount = this._selectedMenus.reduce(
        (prev: number, curr: MenuSelection) => {
          return prev + curr.count;
        },
        0
      );
    }
  }

  public get menusCount(): number {
    return this._menusCount;
  }

  public set menusCount(newMenusCount: number) {
    this._menusCount = newMenusCount;
  }

  public set period(selectedPeriod: string | null) {
    this._period = selectedPeriod;
  }

  public get period(): string | null {
    return this._period;
  }

  public set area(selectedArea: string | null) {
    this._area = selectedArea;
  }

  public get area(): string | null {
    return this._area;
  }

  public resetSelectedArea(engineMode: EngineMode) {
    if (EngineMode.capacity === engineMode) {
      this.area = null;
    }
  }

  public updateCalendateDate(numberOfMonthToAdd: number) {
    let newCalendarDate = this.calendarDate
      .clone()
      .add(numberOfMonthToAdd, "months")
      .startOf("month");
    if (this.calendarDate !== newCalendarDate) {
      this.calendarDate = moment(newCalendarDate);
    }
  }

  public updateDate(momentDate: any) {
    if (this.date?.format("D MMMM YYYY") !== momentDate.format("D MMMM YYYY")) {
      this.date = momentDate;
    }
  }

  public get calendarDate(): moment.Moment {
    return this._calendarDate;
  }

  public set calendarDate(newDate: moment.Moment) {
    this._calendarDate = newDate;
  }

  public get backButtonClicked(): boolean {
    return this._backButtonClicked;
  }

  public set backButtonClicked(isClicked) {
    this._backButtonClicked = isClicked;
  }

  //TODO see with fred is still needed
  public clearStep(currentStep: SelectionSteps) {
    switch (currentStep) {
      case SelectionSteps.guests:
        this.guests = 0;
        this.children = 0;
        break;
      case SelectionSteps.date:
        this.date = moment().subtract(1, "days");
        break;
      case SelectionSteps.time:
        this.time = "";
        break;
      default:
        break;
    }
  }

  //TODO to see with fred
  //selectionCalendarDateService with now reset calendarDate also
  public resetDate() {
    this.date = moment(null);
    this.time = "";
  }
}

export const selectionStore = new SelectionStore();
